import { useEffect, useState } from 'react';
import {
  DivideCircle,
  CheckCircle,
  Clock,
  DollarSign,
  Play,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PageHeader from '../../../components/common/PageHeader';
import StatsCard from '../../../components/common/StatsCard';
import EmptyState from '../../../components/common/EmptyState';
import StatusChip from '../components/shared/StatusChip';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import PAMMErrorState from '../components/shared/PAMMErrorState';
import { DataTable } from '../../../components/ui/data-table';
import { pammDistributionService } from '../services/pammService';
import { formatCurrency, formatDateTime, exportToCSV } from '../utils';
import type { ProfitDistribution } from '../types';
import type { DataTableColumn } from '../../../components/ui/data-table';
import toast from 'react-hot-toast';

export default function ProfitDistributionPage() {
  const [distributions, setDistributions] = useState<ProfitDistribution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setDistributions(await pammDistributionService.getAll());
    } catch {
      setError('Failed to load distributions.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, []);

  const handleDistribute = async (id: string) => {
    try {
      await pammDistributionService.distribute(id);
      setDistributions(prev =>
        prev.map(d =>
          d.id === id ? { ...d, status: 'distributed' as const } : d
        )
      );
      toast.success('Profit distributed successfully!');
    } catch {
      toast.error('Failed to distribute profit.');
    }
  };

  const distributed = distributions.filter(d => d.status === 'distributed');
  const pending = distributions.filter(d => d.status === 'pending');
  const totalDistributed = distributed.reduce((s, d) => s + d.netProfit, 0);

  const columns: DataTableColumn<ProfitDistribution>[] = [
    {
      id: 'fund',
      header: 'Fund',
      accessor: row => (
        <span className="font-medium text-xs">{row.fundName}</span>
      ),
      sortable: true,
      sortValue: row => row.fundName,
    },
    {
      id: 'period',
      header: 'Period',
      accessor: row => <span className="text-xs">{row.period}</span>,
    },
    {
      id: 'totalProfit',
      header: 'Gross Profit',
      accessor: row => (
        <span className="text-xs font-medium text-green-600">
          {formatCurrency(row.totalProfit)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.totalProfit,
    },
    {
      id: 'mgmtFee',
      header: 'Mgmt Fee',
      accessor: row => (
        <span className="text-xs text-red-500">
          -{formatCurrency(row.managementFeeDeducted)}
        </span>
      ),
    },
    {
      id: 'perfFee',
      header: 'Perf Fee',
      accessor: row => (
        <span className="text-xs text-red-500">
          -{formatCurrency(row.performanceFeeDeducted)}
        </span>
      ),
    },
    {
      id: 'netProfit',
      header: 'Net Profit',
      accessor: row => (
        <span className="text-xs font-semibold">
          {formatCurrency(row.netProfit)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.netProfit,
    },
    {
      id: 'investors',
      header: 'Investors',
      accessor: row => <span className="text-xs">{row.investorCount}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <StatusChip status={row.status} size="sm" />,
    },
    {
      id: 'actions',
      header: '',
      accessor: row =>
        row.status === 'pending' ? (
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-xs"
            onClick={() => handleDistribute(row.id)}
          >
            <Play className="h-3 w-3 mr-1" />
            Distribute
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">
            {row.distributedAt ? formatDateTime(row.distributedAt) : '—'}
          </span>
        ),
    },
  ];

  if (loading)
    return (
      <div className="space-y-4">
        <PageHeader title="Profit Distribution" />
        <LoadingSkeleton type="table" />
      </div>
    );
  if (error)
    return (
      <div className="space-y-4">
        <PageHeader title="Profit Distribution" />
        <PAMMErrorState message={error} onRetry={load} />
      </div>
    );

  return (
    <div className="space-y-6 pb-6">
      <PageHeader
        title="Profit Distribution"
        description="Manage and distribute profits to PAMM fund investors"
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Distributions"
          value={distributions.length}
          icon={DivideCircle}
        />
        <StatsCard
          title="Distributed"
          value={distributed.length}
          icon={CheckCircle}
        />
        <StatsCard title="Pending" value={pending.length} icon={Clock} />
        <StatsCard
          title="Total Distributed"
          value={formatCurrency(totalDistributed, 'USD', true)}
          icon={DollarSign}
        />
      </div>
      {distributions.length === 0 ? (
        <EmptyState
          icon={DivideCircle}
          title="No Distributions"
          description="Profit distributions will appear here."
        />
      ) : (
        <DataTable
          columns={columns}
          data={distributions}
          showColumnVisibility={true}
          searchValue={searchValue}
          searchPlaceholder="Search distributions..."
          onSearchChange={setSearchValue}
          onSearchReset={() => setSearchValue('')}
          activeFilters={activeFilters}
          onFiltersChange={setActiveFilters}
          showExportButton={true}
          exportButtonText="Export CSV"
          onExportClick={() =>
            exportToCSV(distributions, 'profit_distributions')
          }
        />
      )}
    </div>
  );
}
